
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import axios from '@/plugins/request';
import PackingSlipPrintTemplate from '@/components/Purchasing/PackingSlipPrintTemplate.vue';

@Component({
  components: {
    PackingSlipPrintTemplate,
  },
})
export default class AutoPrintPackingSlip extends Vue {
  @Prop({ type: Number, required: true }) purchaseOrderId!: number;
  @Prop({ type: String, required: true }) printToken!: string;
  @Prop({ type: String, default: null }) printedBy!: string | null;

  loading = true;
  validated = false;
  error = '';
  printReady = false;

  async mounted() {
    await this.validateToken();
  }

  async validateToken() {
    this.loading = true;
    this.error = '';
    this.validated = false;

    if (!this.printToken) {
      this.error = 'No print token provided';
      this.loading = false;
      return;
    }

    try {
      const response = await axios.post('/api/print/validate-token', {
        token: this.printToken,
        documentType: 'PackingSlip',
        documentId: this.purchaseOrderId,
      });

      if (response.data.valid) {
        console.log('[AutoPrintPackingSlip] Token validated for PO:', this.purchaseOrderId);

        // Always bind the store reference — required on every fresh page load
        // because $systemCategories is a fresh module-level object each time (#1264).
        this.$systemCategories.load(this);

        // Always fetch fresh system categories to avoid stale localStorage data
        await this.$store.dispatch('retrievesystemCategories', this);

        this.validated = true;
        // Start watching for print template loading state
        this.$nextTick(() => this.watchPrintTemplateLoading());
      } else {
        this.error = response.data.reason || 'Invalid or expired print token';
        console.error('[AutoPrintPackingSlip] Token validation failed:', response.data.reason);
      }
    } catch (err: any) {
      console.error('[AutoPrintPackingSlip] Token validation error:', err);

      if (err.response?.status === 404) {
        this.error = 'Print token not found or already used';
      } else if (err.response?.data?.reason) {
        this.error = err.response.data.reason;
      } else {
        this.error = 'Failed to validate print token';
      }
    } finally {
      this.loading = false;
    }
  }

  watchPrintTemplateLoading() {
    // Poll the print template's loading state
    // Once template.loading is false, the iframes are created
    // Then we wait for the pagedjs-complete event from each iframe
    const checkLoading = () => {
      const template = this.$refs.printTemplate as any;
      if (template && !template.loading) {
        console.log('[AutoPrintPackingSlip] Template loaded, waiting for PagedJS completion...');
        // Fallback in case PagedJS completion event doesn't fire (e.g., render error).
        // Normal completion is event-driven via onPagedJsComplete and typically fires
        // in <5s. This 25s fallback accommodates complex packing slips that may
        // legitimately take 20+ seconds to render. Kept below Puppeteer's
        // PageRenderTimeoutSeconds (45s) so Vue signals ready first even if PagedJS
        // genuinely hangs.
        setTimeout(() => {
          if (!this.printReady) {
            console.warn('[AutoPrintPackingSlip] PagedJS completion timeout, setting ready anyway');
            this.printReady = true;
          }
        }, 25000); // 25 second fallback timeout
      } else if (!this.printReady) {
        setTimeout(checkLoading, 500);
      }
    };
    checkLoading();
  }

  onPagedJsComplete() {
    console.log('[AutoPrintPackingSlip] PagedJS complete event received, print ready');
    this.printReady = true;
  }

  @Watch('purchaseOrderId')
  onPurchaseOrderIdChange() {
    this.printReady = false;
    this.validateToken();
  }
}
